import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Dropdown } from '../ui/dropdown/Dropdown';
import { DropdownItem } from '../ui/dropdown/DropdownItem';

export default function UserDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = () => {
    logout();
    navigate('/signin');
  };

  function toggleDropdown() {
    setIsOpen(!isOpen);
  }

  function closeDropdown() {
    setIsOpen(false);
  }

  if (!user) {
    return <div>No user data</div>;
  }

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        className="flex items-center gap-3 rounded-2xl border border-[rgb(var(--app-border)/0.68)] bg-[rgb(var(--app-surface)/0.9)] px-2.5 py-2 transition-all hover:border-[rgb(var(--app-brand)/0.2)]"
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[rgb(var(--app-brand)/0.14)] text-[rgb(var(--app-brand-strong))]">
          <span className="text-xs font-bold uppercase">
            {user?.first_name?.[0] || 'U'}
            {user?.last_name?.[0] || 'S'}
          </span>
        </div>
        <div className="hidden min-w-0 text-left sm:block">
          <div className="truncate text-sm font-semibold text-[rgb(var(--app-text))]">
            {user?.first_name}
          </div>
          <div className="truncate text-xs text-[rgb(var(--app-muted))]">
            {user?.role || 'Team member'}
          </div>
        </div>
        <svg
          className="h-4 w-4 text-[rgb(var(--app-muted))]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className="absolute right-0 mt-4 flex w-[280px] flex-col rounded-[24px] border border-[rgb(var(--app-border)/0.72)] bg-[rgb(var(--app-surface)/0.98)] p-3 shadow-[0_22px_48px_rgba(15,23,42,0.14)]"
      >
        <div>
          <span className="block text-theme-sm font-medium text-[rgb(var(--app-text))]">
            {user?.first_name} {user?.last_name}
          </span>
          <span className="mt-0.5 block text-theme-xs text-[rgb(var(--app-muted))]">
            {user?.email}
          </span>
        </div>

        <ul className="flex flex-col gap-1 border-b border-[rgb(var(--app-border)/0.62)] pb-3 pt-4">
          <li>
            <DropdownItem
              onItemClick={closeDropdown}
              tag="a"
              href="/profile"
              className="group flex items-center gap-3 rounded-xl px-3 py-2.5 font-medium text-[rgb(var(--app-muted))] text-theme-sm hover:bg-[rgb(var(--app-surface-strong)/0.82)] hover:text-[rgb(var(--app-text))]"
            >
              <svg
                className="fill-current text-[rgb(var(--app-text))]"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.0002 2.29199C7.03683 2.29199 4.63433 4.69449 4.63433 7.65783C4.63433 10.6212 7.03683 13.0237 10.0002 13.0237C12.9635 13.0237 15.366 10.6212 15.366 7.65783C15.366 4.69449 12.9635 2.29199 10.0002 2.29199ZM10.0002 3.95866C12.0447 3.95866 13.6993 5.61324 13.6993 7.65783C13.6993 9.70241 12.0447 11.357 10.0002 11.357C7.95558 11.357 6.301 9.70241 6.301 7.65783C6.301 5.61324 7.95558 3.95866 10.0002 3.95866Z"
                  fill=""
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.0002 13.0237C7.51891 13.0237 5.12558 13.9387 3.30225 15.762C1.47891 17.5854 0.563916 19.9787 0.563916 22.46C0.563916 22.8837 0.90725 23.2271 1.33099 23.2271H18.6693C19.0931 23.2271 19.4364 22.8837 19.4364 22.46C19.4364 19.9787 18.5214 17.5854 16.6981 15.762C14.8747 13.9387 12.4814 13.0237 10.0002 13.0237ZM2.26891 21.5604C2.45891 19.6837 3.23225 17.9229 4.51891 16.6362C5.97891 15.1762 7.93891 14.3904 10.0002 14.3904C12.0614 14.3904 14.0214 15.1762 15.4814 16.6362C16.7681 17.9229 17.5414 19.6837 17.7314 21.5604H2.26891Z"
                  fill=""
                />
              </svg>
              View Profile
            </DropdownItem>
          </li>
          <li>
            <DropdownItem
              onItemClick={closeDropdown}
              tag="a"
              href="/billing"
              className="group flex items-center gap-3 rounded-xl px-3 py-2.5 font-medium text-[rgb(var(--app-muted))] text-theme-sm hover:bg-[rgb(var(--app-surface-strong)/0.82)] hover:text-[rgb(var(--app-text))]"
            >
              <svg
                className="stroke-current text-[rgb(var(--app-text))]"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Usage &amp; Billing
            </DropdownItem>
          </li>
          {(user?.manageable_billing_groups?.length ?? 0) > 0 && (
            <li>
              <DropdownItem
                onItemClick={closeDropdown}
                tag="a"
                href="/billing/group"
                className="group flex items-center gap-3 rounded-xl px-3 py-2.5 font-medium text-[rgb(var(--app-muted))] text-theme-sm hover:bg-[rgb(var(--app-surface-strong)/0.82)] hover:text-[rgb(var(--app-text))]"
              >
                <svg
                  className="stroke-current text-[rgb(var(--app-text))]"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6-1a3 3 0 10-3-3m-9 3a3 3 0 113-3"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Group Billing
              </DropdownItem>
            </li>
          )}
        </ul>

        <ul className="flex flex-col gap-1 pt-3">
          <li>
            <DropdownItem
              onItemClick={handleSignOut}
              className="group flex items-center gap-3 rounded-xl px-3 py-2.5 font-medium text-[rgb(var(--app-muted))] text-theme-sm hover:bg-[rgb(var(--app-surface-strong)/0.82)] hover:text-[rgb(var(--app-text))]"
            >
              <svg
                className="stroke-current text-[rgb(var(--app-text))]"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.90002 7.55999C9.21002 3.95999 11.06 2.48999 15.11 2.48999H15.24C19.71 2.48999 21.5 4.27999 21.5 8.74999V15.27C21.5 19.74 19.71 21.53 15.24 21.53H15.11C11.09 21.53 9.24002 20.08 8.91002 16.54"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 12H3.62"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5.85 8.6499L2.5 11.9999L5.85 15.3499"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Sign Out
            </DropdownItem>
          </li>
        </ul>
      </Dropdown>
    </div>
  );
}
